/**
 * Slug generation & validation for room ids. Mirrors server/src/slug.ts
 * exactly so that both ends agree on what's valid and what a generated
 * slug looks like. Keep these two files in sync.
 *
 * Constraints (SPEC §11):
 *   - lowercase, alphanumeric + dash only
 *   - 1..32 chars
 *   - cannot be the literal "template" (reserved)
 */

const ADJECTIVES: ReadonlyArray<string> = [
  "amber",
  "ancient",
  "azure",
  "brave",
  "bright",
  "calm",
  "clever",
  "cosmic",
  "crystal",
  "curious",
  "dazzling",
  "eager",
  "electric",
  "emerald",
  "fancy",
  "feisty",
  "fierce",
  "frosty",
  "gentle",
  "glowing",
  "golden",
  "grumpy",
  "happy",
  "hidden",
  "humble",
  "jolly",
  "lively",
  "lucky",
  "mighty",
  "misty",
  "noble",
  "playful",
  "quiet",
  "rapid",
  "rusty",
  "scarlet",
  "silent",
  "silver",
  "sleepy",
  "sneaky",
  "snowy",
  "stormy",
  "sunny",
  "swift",
  "tiny",
  "velvet",
  "vibrant",
  "wandering",
  "wild",
  "witty",
];

const NOUNS: ReadonlyArray<string> = [
  "atlas",
  "badger",
  "beacon",
  "buffalo",
  "cactus",
  "canyon",
  "comet",
  "crane",
  "delta",
  "ember",
  "falcon",
  "ferret",
  "forest",
  "fox",
  "galaxy",
  "garden",
  "geyser",
  "glacier",
  "harbor",
  "horizon",
  "iris",
  "island",
  "jackal",
  "lagoon",
  "lantern",
  "lynx",
  "marble",
  "meadow",
  "mountain",
  "nimbus",
  "ocean",
  "orchard",
  "otter",
  "panda",
  "phoenix",
  "puma",
  "quokka",
  "raven",
  "river",
  "saber",
  "summit",
  "tiger",
  "tundra",
  "valley",
  "vortex",
  "walrus",
  "willow",
  "wombat",
  "yeti",
  "zebra",
];

export const RESERVED_SLUGS: ReadonlySet<string> = new Set(["template", ""]);
export const SLUG_MAX_LENGTH = 32;
const SLUG_PATTERN = /^[a-z0-9]+(-[a-z0-9]+)*$/;

export interface ValidationOk {
  ok: true;
  slug: string;
}

export interface ValidationErr {
  ok: false;
  reason: string;
}

export type ValidationResult = ValidationOk | ValidationErr;

export function validateSlug(candidate: unknown): ValidationResult {
  if (typeof candidate !== "string") {
    return { ok: false, reason: "slug must be a string" };
  }
  const raw = candidate.trim().replace(/^#/, "");
  if (raw.length === 0) {
    return { ok: false, reason: "slug is empty" };
  }
  if (raw.length > SLUG_MAX_LENGTH) {
    return { ok: false, reason: `slug exceeds ${SLUG_MAX_LENGTH} chars` };
  }
  if (RESERVED_SLUGS.has(raw)) {
    return { ok: false, reason: `slug "${raw}" is reserved` };
  }
  if (!SLUG_PATTERN.test(raw)) {
    return {
      ok: false,
      reason: "slug must be lowercase alphanumeric with dashes; no leading/trailing/consecutive dashes",
    };
  }
  return { ok: true, slug: raw };
}

export function generateSlug(rng: () => number = Math.random): string {
  const adj = pick(ADJECTIVES, rng);
  const noun = pick(NOUNS, rng);
  return `${adj}-${noun}`;
}

function pick<T>(arr: ReadonlyArray<T>, rng: () => number): T {
  const idx = Math.floor(rng() * arr.length);
  const safe = Math.min(arr.length - 1, Math.max(0, idx));
  return arr[safe]!;
}
